import axios from 'axios';
import { User, LoginResponse, Course, Progress, FullCourse, CourseTest, CourseModule, MCQQuestion, TestType, TestAttemptResult } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('sedstart_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('sedstart_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  getAuthUrls: () => api.get<{ google: string; microsoft: string; linkedin: string }>('/auth/urls'),
  loginWithGoogle: (code: string, role?: string) => api.post<LoginResponse>('/auth/google', { code, role }),
  loginWithMicrosoft: (code: string, role?: string) => api.post<LoginResponse>('/auth/microsoft', { code, role }),
  loginWithLinkedIn: (code: string) => api.post<LoginResponse>('/auth/linkedin', { code }),
  validateToken: () => api.get('/auth/validate'),
  getProfile: () => api.get<User>('/auth/me'),
  updateProfile: (profile: Partial<User>) => api.put('/auth/profile', profile),
  logout: () => api.post('/auth/logout'),
};

export const learnerAPI = {
  getProgress: () => api.get<{ progress: Progress[] }>('/learner/progress'),
  getCourseProgress: (courseId: string) => api.get<{
    id?: number; user_id?: number; course_id?: number;
    current_module_id?: number; completed_modules: number[];
    progress_percent: number; is_completed: boolean;
  }>(`/learner/progress/${courseId}`),
  getCourses: () => api.get<{ courses: FullCourse[] }>('/learner/courses'),
  getCourseDetail: (courseId: string) => api.get<{ course: FullCourse }>(`/learner/courses/${courseId}`),
  updateProgress: (moduleId: number, timeSpent: number) =>
    api.post('/learner/progress', { module_id: moduleId, time_spent: timeSpent }),
  submitTestAttempt: (
    courseId: string,
    type: TestType,
    data: { answers: { question_id: string; answer: number }[]; time_spent: number }
  ) => api.post<TestAttemptResult>(`/learner/tests/${type}/attempt`, { course_id: courseId, ...data }),
};

export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getAnalytics: () => api.get('/admin/analytics/overview'),
  createUser: (data: { email: string; name: string; role: string }) => api.post('/admin/users', data),
  updateUser: (userId: number, data: { name: string; role: string }) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId: number) => api.delete(`/admin/users/${userId}`),
  resetUserProgress: (userId: number) => api.put(`/admin/users/${userId}/progress/reset`),
  inviteLearners: (courseId: string, emails: string[]) =>
    api.post('/admin/invite', { course_id: courseId, emails }),
};

export const contentAPI = {
  // ── Courses ──────────────────────────────────────────────────────────────
  getCourses: () => api.get<{ courses: FullCourse[] }>('/content/courses'),
  getCourse: (courseId: string) => api.get<{ course: FullCourse }>(`/content/courses/${courseId}`),
  createCourse: (course: Partial<FullCourse>) => api.post<{ course: FullCourse }>('/content/courses', course),
  updateCourse: (courseId: string, course: Partial<FullCourse>) =>
    api.put<{ course: FullCourse }>(`/content/courses/${courseId}`, course),
  deleteCourse: (courseId: string) => api.delete(`/content/courses/${courseId}`),
  publishCourse: (courseId: string) => api.post<{ course: FullCourse }>(`/content/courses/${courseId}/publish`),

  // ── Modules ───────────────────────────────────────────────────────────────
  createModule: (courseId: string, module: Partial<CourseModule>) =>
    api.post<{ module: CourseModule }>(`/content/courses/${courseId}/modules`, module),
  updateModule: (courseId: string, moduleId: string, module: Partial<CourseModule>) =>
    api.put<{ module: CourseModule }>(`/content/courses/${courseId}/modules/${moduleId}`, module),
  deleteModule: (courseId: string, moduleId: string) =>
    api.delete(`/content/courses/${courseId}/modules/${moduleId}`),
  reorderModules: (courseId: string, order: string[]) =>
    api.post(`/content/courses/${courseId}/modules/reorder`, { order }),

  // ── Tests ─────────────────────────────────────────────────────────────────
  createTest: (courseId: string, test: Partial<CourseTest>) =>
    api.post<{ test: CourseTest }>(`/content/courses/${courseId}/tests`, test),
  updateTest: (courseId: string, type: TestType, test: Partial<CourseTest>) =>
    api.put<{ test: CourseTest }>(`/content/courses/${courseId}/tests/${type}`, test),
  deleteTest: (courseId: string, type: TestType) =>
    api.delete(`/content/courses/${courseId}/tests/${type}`),

  // ── File Upload ───────────────────────────────────────────────────────────
  uploadFile: (file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ url: string; filename: string; size: number; content_type: string }>(
      '/content/upload',
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      }
    );
  },

  // ── Questions ─────────────────────────────────────────────────────────────
  addQuestion: (courseId: string, type: TestType, q: Partial<MCQQuestion>) =>
    api.post<{ question: MCQQuestion }>(`/content/courses/${courseId}/tests/${type}/questions`, q),
  updateQuestion: (courseId: string, type: TestType, questionId: string, q: Partial<MCQQuestion>) =>
    api.put<{ question: MCQQuestion }>(`/content/courses/${courseId}/tests/${type}/questions/${questionId}`, q),
  deleteQuestion: (courseId: string, type: TestType, questionId: string) =>
    api.delete(`/content/courses/${courseId}/tests/${type}/questions/${questionId}`),
};

export const courseAPI = {
  getCourses: () => api.get<{ courses: Course[] }>('/courses'),
  getCourse: (courseId: number) => api.get(`/courses/${courseId}`),
};

export default api;
